// @ts-nocheck

import React from 'react';
import styled, { css } from 'styled-components';

import { __ } from '../../i18n/l10n';
import BoomBasketBetslipBandContainer from '../../modules/boom/components/BoomBasketBetslip/BoomBasketBetslipContainer';
import GRBasketBetslipEligibleHeadBandContainer from '../../modules/gift-race/components/GRBasketBetslipBand/GRBasketBetslipBandContainer';
import KLBasketBetslipEligibleCollectibleContainer from '../../modules/koh-lanta/containers/KLBasketBetslipEligibleCollectibleContainer';
import BasketBetslipMissionEligibleTagContainer from '../../modules/missions/components/BasketBetslipMissionEligibleTag/BasketBetslipMissionEligibleTagContainer';
import MPBasketBetslipBandContainer from '../../modules/monopoly/containers/MPBasketBetslipBandContainer';
import { type BetBuilderItem } from '../../states/betbuilder';
import type { BetState } from '../../states/entities/bets';
import type { OutcomeState } from '../../states/entities/outcomes';
import theme from '../../styled-components/dark';
import type { BasketItemStatus, BasketMode } from '../../types/basket';
import { BasketModes, BasketStatuses, type OddsChange } from '../../types/basket';
import { type CardMode } from '../../types/common';
import type { SportId } from '../../types/sports';
import BasketAlert from '../BasketAlert';
import BasketCardEligibleFreebets from '../BasketCardEligibleFreebets';
import BasketCardHeader from '../BasketCardHeader';
import CardMyMatchContentContainer from '../BasketCardMyMatchContent/BasketCardMyMatchContentContainer';
import BasketCardOdds from '../BasketCardOdds';
import BasketCardPotentialContent from '../BasketCardPotentialContent';
import BasketCardPreviousOdd from '../BasketCardPreviousOdd';
import BasketCardSimpleContent from '../BasketCardSimpleContent';
import BasketCardSystemContent from '../BasketCardSystemContent';
import BasketCardTitle from '../BasketCardTitle';
import InfosLabel, { InfosLabelTypes } from '../InfosLabel/InfosLabel';

const Wrapper = styled.div`
  margin: 0 0.6em;
  padding: 0.6em 0 0;
  color: #fff;
`;

const Content = styled.div`
  position: relative;
  overflow: hidden;
  padding: 0.5em 0.5em 0.3em;
  background-color: ${theme.BasketHeader.backgroundColor};
  border-radius: 0 0 10px 10px;
`;

const marginSharedStyle = css`
  margin-bottom: 0.4em;
`;

const StyledAlert = styled(BasketAlert)`
  ${marginSharedStyle}
`;

const StyledGRBasketBetslipEligibleHeadBandContainer = styled(GRBasketBetslipEligibleHeadBandContainer)`
  ${marginSharedStyle}
`;

const StyledKLBasketBetslipEligibleCollectibleContainer = styled(KLBasketBetslipEligibleCollectibleContainer)`
  ${marginSharedStyle}
`;

const StyledBasketBetslipMissionEligibleTagContainer = styled(BasketBetslipMissionEligibleTagContainer)`
  ${marginSharedStyle}
`;

const StyledMPBasketBetslipContainer = styled(MPBasketBetslipBandContainer)`
  ${marginSharedStyle}
`;

const StyledBoomBasketBetslipBandContainer = styled(BoomBasketBetslipBandContainer)`
  ${marginSharedStyle}
`;

const StyledInfosLabel = styled(InfosLabel)`
  margin: 0 0 0.5em 0;
  align-items: center;
`;

type ConditialAlertProps = {
  isBasketSimpleMode: boolean;
  isShowingMainBetConfirmation: boolean;
  alertContent?: string;
  status: BasketItemStatus;
  showBetshareButton: boolean;
  nbItem: number;
  isValidating: boolean;
  betslipId?: string;
};

const ConditionalAlert = React.memo<ConditialAlertProps>(function ConditionalAlert({
  isBasketSimpleMode,
  isShowingMainBetConfirmation,
  alertContent,
  status,
  showBetshareButton,
  nbItem,
  isValidating,
  betslipId,
}: ConditialAlertProps) {
  if (!isShowingMainBetConfirmation && alertContent) {
    if (~[BasketStatuses.ERROR, BasketStatuses.REJECTED].indexOf(status)) {
      return <StyledInfosLabel label={alertContent} symbol={'!'} type={InfosLabelTypes.INFOS_RED} />;
    }

    return (
      <StyledAlert message={alertContent} status={status} betslipReference={showBetshareButton ? betslipId : null} />
    );
  }

  if (isBasketSimpleMode && nbItem > 1 && isValidating) {
    return (
      <StyledAlert message={__('Bet validation in progress')} status={BasketStatuses.PENDING} betslipReference={null} />
    );
  }
  return null;
});

type BasketCardProps = {
  selectionItems: Array<{
    bet: BetState;
    outcome: OutcomeState;
    outcomes: Array<OutcomeState>;
    item: BetBuilderItem;
  }>;
  potentialGain: number;
  betTitle?: string;
  previousOdd?: number;
  outcomeLabel?: string;
  basketMode: BasketMode;
  nbItem: number;
  showBetshareButton: boolean;
  readOnly: boolean;
  title: string;
  sportId: SportId;
  categoryId: number;
  isLive: boolean;
  alertContent?: string;
  banker?: boolean;
  status: BasketItemStatus;
  betslipId?: string;
  value: number;
  changed?: OddsChange;
  validatedValue?: number;
  oddId: number;
  isValidating: boolean;
  handleDelItem: (...params: unknown) => unknown;
  handleSelectBanker: (...params: unknown) => unknown;
  handleCheckEligibleFreebets: (...params: unknown) => unknown;
  cardMode: CardMode;
  eligibleFreebets: {
    [key: string]: {
      freebetId: number;
      isUsed: boolean;
    };
  };
  freebets: {
    [key: string]: {
      freebetId: number;
      amount: number;
      rule: string;
      freebetCodeId: string;
      title: string;
    };
  };
  selectedFreebetId: number;
  isShowingMainBetConfirmation: boolean;
  isBasketItemEligibleToAnyMission: boolean;
  isUserConnected: boolean;
  isMyMatch?: boolean;
  myMatchErrorLabel?: string;
  itemId?: string;
};

const BasketCard = ({
  basketMode,
  outcomeLabel = '',
  betTitle = '',
  previousOdd,
  nbItem,
  readOnly,
  title,
  sportId,
  categoryId,
  isLive,
  showBetshareButton,
  alertContent,
  handleDelItem,
  handleSelectBanker,
  handleCheckEligibleFreebets,
  banker = false,
  status,
  betslipId,
  value,
  changed,
  validatedValue,
  oddId,
  selectionItems,
  potentialGain,
  isValidating,
  cardMode,
  eligibleFreebets,
  freebets,
  selectedFreebetId,
  isShowingMainBetConfirmation,
  isMyMatch = false,
  myMatchErrorLabel,
  itemId = '',
}: BasketCardProps) => {
  const isBasketSimpleMode = basketMode === BasketModes.SIMPLE;
  const isBasketCombinedMode = basketMode === BasketModes.COMBINED;

  return (
    <Wrapper data-testid={`item-${itemId}`}>
      {myMatchErrorLabel && basketMode !== BasketModes.SIMPLE ? (
        <StyledInfosLabel label={myMatchErrorLabel} symbol={'!'} type={InfosLabelTypes.INFOS_RED} />
      ) : null}
      <ConditionalAlert
        isShowingMainBetConfirmation={isShowingMainBetConfirmation}
        isBasketSimpleMode={isBasketSimpleMode}
        isBasketCombinedMode={isBasketCombinedMode}
        nbItem={nbItem}
        isValidating={isValidating}
        alertContent={alertContent}
        status={status}
        showBetshareButton={showBetshareButton}
        betslipId={betslipId}
        oddId={oddId}
      />
      {!isBasketCombinedMode ? <StyledGRBasketBetslipEligibleHeadBandContainer oddsId={oddId} /> : null}
      {!isBasketCombinedMode ? <StyledKLBasketBetslipEligibleCollectibleContainer oddsId={oddId} /> : null}
      {!isBasketCombinedMode ? <StyledBasketBetslipMissionEligibleTagContainer oddsId={oddId} /> : null}
      {!isBasketCombinedMode ? <StyledMPBasketBetslipContainer oddsId={oddId} /> : null}
      {!isBasketCombinedMode ? <StyledBoomBasketBetslipBandContainer oddsId={oddId} /> : null}
      <BasketCardHeader
        title={title}
        sportId={sportId}
        categoryId={categoryId}
        onClickClose={handleDelItem}
        isLive={isLive}
        cardMode={cardMode}
      />
      <Content>
        {isMyMatch ? (
          <CardMyMatchContentContainer />
        ) : (
          <BasketCardTitle betTitle={betTitle} outcomeLabel={outcomeLabel} selectionItems={selectionItems} />
        )}
        {previousOdd ? <BasketCardPreviousOdd previousOdd={previousOdd} /> : null}
        {basketMode === BasketModes.SYSTEM ? (
          <BasketCardSystemContent banker={banker} handleSelectBanker={handleSelectBanker} readOnly={readOnly} />
        ) : null}
        {isBasketSimpleMode ? (
          <BasketCardEligibleFreebets
            eligibleFreebets={eligibleFreebets}
            isBasketSimpleMode={isBasketSimpleMode}
            isBasketCombinedMode={isBasketCombinedMode}
            nbItem={nbItem}
            freebets={freebets}
            oddId={oddId}
            handleCheckEligibleFreebets={handleCheckEligibleFreebets}
            selectedFreebetId={selectedFreebetId}
          />
        ) : (
          <BasketCardOdds
            basketMode={basketMode}
            status={status}
            validatedValue={validatedValue}
            value={value}
            changed={changed}
            oddId={oddId}
          />
        )}
        <BasketCardSimpleContent
          isBasketSimpleMode={isBasketSimpleMode}
          status={status}
          nbItem={nbItem}
          oddId={oddId}
          changed={changed}
          value={value}
          selectedFreebetId={selectedFreebetId}
          validatedValue={validatedValue}
        />
        {nbItem > 1 && isBasketSimpleMode ? <BasketCardPotentialContent potentialGain={potentialGain} /> : null}
      </Content>
    </Wrapper>
  );
};

export default BasketCard;
